import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, FormControl, TextField, MenuItem, Select, Grid } from '@mui/material';
import { FaPlus } from 'react-icons/fa';
import * as d3 from 'd3';

import { teamsApi, employeesApi } from '../../../../../services/api';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import Modal, { Field, Label } from '../../../../common/Modal';
import EmployeeSearch from '../../../../common/EmployeeSearch';
import { getInitials } from '../../../../common/EmployeeAvatar';
import { HeaderButton } from '../../../../common/styles';
import { FormContainer, Content } from '../../styles';
import { EMPLOYEE_STATUSES, getStatusColor, getStatusTextColor } from '../../../../../constants/employeeStatuses';
import EmptyState from '../../../../common/EmptyState';
import ManagerHistoryModal from '../../../../common/ManagerHistoryModal';
import { MANAGER_HISTORY_ENTITY_TYPES } from '../../../../../constants/managerHistory';

const TeamStructure = ({ projectId, showAlert, readOnly = false, onProjectManagerChange }) => {
  const svgRef = useRef();
  const [teamHierarchy, setTeamHierarchy] = useState(null);
  const [isTeamHierarchyLoading, setIsTeamHierarchyLoading] = useState(false);
  const [expandAll, setExpandAll] = useState(false);

  // Team management state
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Employee move state
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [employeeToMove, setEmployeeToMove] = useState(null);
  const [selectedDestinationTeam, setSelectedDestinationTeam] = useState(null);

  // Team move state
  const [showMoveTeamModal, setShowMoveTeamModal] = useState(false);
  const [teamToMove, setTeamToMove] = useState(null);
  const [selectedDestinationParentTeam, setSelectedDestinationParentTeam] = useState(null);

  // History modal state
  const [historyModal, setHistoryModal] = useState({ open: false, entityId: null, entityName: '', entityType: null });

  // Function to get all teams for selection with hierarchy level
  const getAllTeams = (team, level = 0, teams = [], parentId = null) => {
    if (team) {
      teams.push({
        id: team.id,
        name: team.name,
        level,
        parentId,
        childTeamIds: team.childTeams ? team.childTeams.map(child => child.id) : []
      });
      if (team.childTeams) {
        team.childTeams.forEach(child => getAllTeams(child, level + 1, teams, team.id));
      }
    }
    return teams;
  };

  // Function to check if a team is in the hierarchy of another team
  const isTeamInHierarchy = (teamId, hierarchyRoot, teamsMap) => {
    if (!hierarchyRoot) return false;
    if (hierarchyRoot.id === teamId) return true;
    
    return hierarchyRoot.childTeamIds.some(childId => 
      isTeamInHierarchy(teamId, teamsMap.get(childId), teamsMap)
    );
  };

  // Function to check if a team should be disabled for move
  const shouldDisableTeamForMove = (team, teamToMove, allTeams) => {
    if (!teamToMove) return false;
    if (team.id === teamToMove.id) return true;

    // Create a map for faster lookups
    const teamsMap = new Map(allTeams.map(t => [t.id, t]));
    
    // Check if the destination team is in the moving team's hierarchy
    return isTeamInHierarchy(team.id, teamsMap.get(teamToMove.id), teamsMap);
  };

  // Function to handle employee move
  const handleMoveEmployee = async () => {
    if (!employeeToMove || !selectedDestinationTeam) return;

    try {
      await teamsApi.moveEmployee({
        projectId,
        employeeId: employeeToMove.id,
        newTeamId: selectedDestinationTeam
      });
      
      showAlert('Individual moved successfully', 'success');
      setShowMoveModal(false);
      setEmployeeToMove(null);
      setSelectedDestinationTeam(null);
      fetchTeamHierarchy();
    } catch (err) {
      showAlert(err.message || 'Failed to move individual', 'error', err);
    }
  };

  // Function to handle team move
  const handleMoveTeam = async () => {
    if (!teamToMove || !selectedDestinationParentTeam) return;

    try {
      await teamsApi.moveTeam({
        sourceTeamId: teamToMove.id,
        destinationTeamId: selectedDestinationParentTeam
      });
      
      showAlert('Team moved successfully', 'success');
      setShowMoveTeamModal(false);
      setTeamToMove(null);
      setSelectedDestinationParentTeam(null);
      fetchTeamHierarchy();
    } catch (err) {
      showAlert(err.message || 'Failed to move team', 'error', err);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchTeamHierarchy();
    }
  }, [projectId]);

  const fetchTeamHierarchy = async () => {
    setIsTeamHierarchyLoading(true);
    try {
      const data = await teamsApi.getTeamHierarchy(projectId);
      const transformTeams = (team) => ({
        ...team,
        children: [
          ...(team.childTeams ? team.childTeams.map(transformTeams) : []),
          ...(team.employees ? team.employees.map(employee => ({
            ...employee,
            isEmployee: true,
            name: employee.fullName
          })) : [])
        ]
      });
      
      const rootTeam = data[0];
      if (rootTeam) {
        setTeamHierarchy(transformTeams(rootTeam));
      } else {
        setTeamHierarchy(null);
      }
    } catch (err) {
      showAlert('Failed to load team hierarchy', 'error', err);
    } finally {
      setIsTeamHierarchyLoading(false);
    }
  };

  const handleTeamModalClose = () => {
    setShowTeamModal(false);
    setSelectedTeam(null);
    setSelectedManager(null);
  };

  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    try {
      const teamData = {
        name: selectedTeam?.name || '',
        managerId: selectedManager?.id || '',
        projectId: projectId,
        parentTeamId: selectedTeam?.parentTeamId
      };

      if (selectedTeam?.id) {
        await teamsApi.updateTeam(selectedTeam.id, teamData);
        showAlert('Team updated successfully', 'success');
      } else {
        await teamsApi.createTeam(teamData);
        showAlert('Team created successfully', 'success');
      }

      handleTeamModalClose();
      fetchTeamHierarchy();
    } catch (err) {
      showAlert(err.message || 'Failed to save team', 'error', err);
    }
  };

  const handleTeamChange = (field, value) => {
    setSelectedTeam(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteConfirm = async () => {
    try {
      await teamsApi.deleteTeam(itemToDelete.id);
      showAlert('Team deleted successfully', 'success');
      fetchTeamHierarchy();
    } catch (err) {
      showAlert(err.message || 'Failed to delete team', 'error', err);
    } finally {
      setShowDeleteConfirmation(false);
      setItemToDelete(null);
    }
  };

  useEffect(() => {
    if (!teamHierarchy || !svgRef.current) return;

    // Clear existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 30, right: 40, bottom: 10, left: 40 };
    const width = 2000;
    const barHeight = 25;
    const treeIndent = 20;

    // Create SVG first
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .style("font", "12px sans-serif");

    // Create main group for tree
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create hierarchy and store original children
    const root = d3.hierarchy(teamHierarchy);
    root.descendants().forEach(d => {
      d._children = d.children;
      d.children = d._children;
    });

    const updateTree = (source) => {
      // Get visible nodes and links
      const nodes = root.descendants();
      const links = root.links();

      // Calculate height based on visible nodes
      const height = nodes.length * barHeight + margin.top + margin.bottom;

      // Update SVG height
      d3.select(svgRef.current)
        .attr("height", height);

      // Calculate positions
      let i = 0;
      root.eachBefore(d => {
        d.x = d.depth * treeIndent;
        d.y = i * barHeight;
        i++;
      });

      // Update links
      const link = g.selectAll(".link")
        .data(links, d => d.target.id || (d.target.id = Math.random()));

      const linkEnter = link.enter()
        .append("path")
        .attr("class", "link")
        .attr("fill", "none")
        .attr("stroke", "#999")
        .attr("stroke-opacity", 0.4)
        .attr("d", d => {
          const o = { x: source.x0 || source.x, y: source.y0 || source.y };
          return `M${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}`;
        });

      link.merge(linkEnter)
        .transition()
        .duration(300)
        .attr("d", d => {
          return `M${d.source.x},${d.source.y + barHeight / 2}
                  L${d.source.x},${d.target.y + barHeight / 2}
                  L${d.target.x},${d.target.y + barHeight / 2}`;
        });

      link.exit()
        .transition()
        .duration(300)
        .attr("d", d => {
          const o = { x: source.x, y: source.y };
          return `M${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}`;
        })
        .remove();

      // Update nodes
      const node = g.selectAll(".node")
        .data(nodes, d => d.id || (d.id = Math.random()));

      const nodeEnter = node.enter()
        .append("g")
        .attr("class", "node")
        .attr("transform", d => `translate(0,${source.y0 || source.y})`)
        .style("opacity", 0);

      // Add expand/collapse button
      nodeEnter.append("g")
        .attr("class", "button-group")
        .attr("transform", d => `translate(${d.x - 15},${barHeight / 2})`)
        .style("cursor", d => d.depth === 0 ? "default" : "pointer")
        .style("display", d => d._children || d.children ? null : "none")
        .on("click", (event, d) => {
          event.stopPropagation();
          if (d.depth !== 0) {
            if (d.children) {
              d._children = d.children;
              d.children = null;
            } else {
              d.children = d._children;
              d._children = null;
            }
            updateTree(d);
          }
        })
        .each(function(d) {
          const group = d3.select(this);
          group.append("circle")
            .attr("r", 7)
            .attr("fill", "white")
            .attr("stroke", "#999");
          
          group.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("font-size", "10px")
            .text(d => d.children ? "−" : "+");
        });

      // Add node group with click handler
      const nodeGroup = nodeEnter.append("g")
        .attr("class", "node-group")
        .style("cursor", readOnly ? "default" : "pointer")
        .on("click", function(event, d) {
          event.stopPropagation();
          
          // If readonly, don't allow selection
          if (readOnly) return;
          
          const allNodeGroups = g.selectAll(".node-group");
          const currentNode = d3.select(this);
          const wasSelected = currentNode.classed("selected");
          
          allNodeGroups.each(function() {
            const node = d3.select(this);
            node.classed("selected", false);
            node.select("text").style("font-weight", "normal");
            node.select(".employee-name").style("font-weight", "normal");
            node.select(".add-subteam-button").style("opacity", 0);
            node.select(".edit-button").style("opacity", 0);
            node.select(".delete-button").style("opacity", 0);
            node.select(".move-button").style("opacity", 0);
            node.select(".history-button").style("opacity", 0);
          });

          if (!wasSelected) {
            currentNode.classed("selected", true);
            currentNode.select("text").style("font-weight", "bold");
            currentNode.select(".employee-name").style("font-weight", "bold");
            currentNode.select(".add-subteam-button").style("opacity", 1);
            currentNode.select(".edit-button").style("opacity", 1);
            currentNode.select(".delete-button").style("opacity", 1);
            currentNode.select(".move-button").style("opacity", 1);
            currentNode.select(".history-button").style("opacity", 1);
          }
        });

      // Add manager avatar if exists
      nodeGroup.each(function(d) {
        const node = d3.select(this);
        let totalWidth = d.x + 8;

        // Different styling for employees
        if (d.data.isEmployee) {
          // First render the node structure without waiting for the image
          // Create a placeholder for the avatar
          const avatarGroup = node.append("g")
            .attr("class", "avatar-placeholder")
            .attr("transform", `translate(${d.x + 8}, ${barHeight / 2})`);
            
          // Add a default circle as placeholder
          avatarGroup.append("circle")
            .attr("r", 8)
            .attr("fill", "#E2E8F0")
            .attr("stroke", "#94A3B8")
            .attr("stroke-width", 1);

          avatarGroup.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "#475569")
            .attr("font-size", "10px")
            .text(getInitials(d.data.name));
            
          // Asynchronously load the photo without blocking
          if (d.data.azureId) {
            employeesApi.getEmployeePhoto(d.data.azureId)
              .then(photoUrl => {
                if (photoUrl) {
                  // Remove the placeholder
                  avatarGroup.selectAll("*").remove();
                  
                  const clipId = `clip-${Math.random().toString(36).substr(2, 9)}`;
                  
                  // Add clip path
                  avatarGroup.append("defs")
                    .append("clipPath")
                    .attr("id", clipId)
                    .append("circle")
                    .attr("r", 8);
                    
                  // Add the image
                  avatarGroup.append("image")
                    .attr("x", -8)
                    .attr("y", -8)
                    .attr("width", 16)
                    .attr("height", 16)
                    .attr("clip-path", `url(#${clipId})`)
                    .attr("preserveAspectRatio", "xMidYMid slice")
                    .attr("xlink:href", photoUrl);
                }
              })
              .catch(error => {
                console.error('Error loading employee photo:', error);
                // Placeholder already rendered, no need to do anything
              });
          }

          totalWidth = d.x + 32; // Space for avatar

          // Add employee name
          const nameElement = node.append("text")
            .attr("class", "employee-name")
            .attr("x", totalWidth)
            .attr("y", barHeight / 2)
            .attr("dy", "0.31em")
            .attr("text-anchor", "start")
            .attr("fill", "#374151")
            .text(d.data.name);

          totalWidth += nameElement.node().getComputedTextLength() + 8;

          // Add status badge
          const statusGroup = node.append("g")
            .attr("transform", `translate(${totalWidth}, ${barHeight / 2 - 10})`);

          const statusText = statusGroup.append("text")
            .attr("x", 8)
            .attr("y", 8)
            .attr("text-anchor", "start")
            .attr("dominant-baseline", "middle")
            .attr("fill", getStatusTextColor(d.data.status))
            .attr("font-size", "12px")
            .text(d.data.status.charAt(0).toUpperCase() + d.data.status.slice(1));

          const textWidth = statusText.node().getComputedTextLength();
          
          statusGroup.insert("rect", "text")
            .attr("x", 0)
            .attr("y", -2)
            .attr("rx", 4)
            .attr("width", textWidth + 16)
            .attr("height", 20)
            .attr("fill", getStatusColor(d.data.status));

          totalWidth += textWidth + 32;

          // Add position and grade in parentheses
          if (d.data.position || d.data.grade) {
            const roleText = node.append("text")
              .attr("x", totalWidth)
              .attr("y", barHeight / 2)
              .attr("dy", "0.31em")
              .attr("text-anchor", "start")
              .attr("fill", "#9CA3AF")
              .text(`(${[d.data.position?.label, d.data.grade?.label].filter(Boolean).join(", ")})`);

            totalWidth += roleText.node().getComputedTextLength() + 16;
          }

          // Add engagement type badge
          if (d.data.engagementType) {
            const badge = node.append("g")
              .attr("transform", `translate(${totalWidth}, ${barHeight / 2 - 10})`);

            const engagementText = badge.append("text")
              .attr("x", 0)
              .attr("y", 8)
              .attr("text-anchor", "start")
              .attr("dominant-baseline", "middle")
              .attr("fill", "#6B7280")
              .attr("font-size", "12px")
              .text(d.data.engagementType.name);

            const textWidth = engagementText.node().getComputedTextLength();
            
            badge.insert("rect", "text")
              .attr("x", -8)
              .attr("y", -2)
              .attr("rx", 4)
              .attr("width", textWidth + 16)
              .attr("height", 20)
              .attr("fill", "#F3F4F6");

            totalWidth += textWidth + 32;
          }

          // Add move button before the totalWidth calculation
          const moveButton = node.append("g")
            .attr("class", "move-button")
            .attr("transform", `translate(${totalWidth}, ${barHeight / 2})`)
            .style("cursor", readOnly ? "default" : (d.data.status === EMPLOYEE_STATUSES.ENGAGED ? "pointer" : "not-allowed"))
            .style("opacity", 0)
            .on("click", (event) => {
              event.stopPropagation();
              if (readOnly) return;
              if (d.data.status === EMPLOYEE_STATUSES.ENGAGED) {
                setEmployeeToMove({
                  id: d.data.id,
                  name: d.data.name,
                  currentTeamId: d.parent.data.id
                });
                setShowMoveModal(true);
              }
            });

          moveButton.append("circle")
            .attr("r", 7)
            .attr("fill", d.data.status === EMPLOYEE_STATUSES.ENGAGED ? "#0EA5E9" : "#94A3B8")
            .attr("stroke", "none");

          moveButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .text("m");

          totalWidth += 25;

          return; // Skip the rest for employees
        }

        // For team nodes
        const textElement = node.append("text")
          .attr("x", totalWidth)
          .attr("y", barHeight / 2)
          .attr("dy", "0.31em")
          .attr("text-anchor", "start")
          .text(d.depth === 0 ? `Project: ${d.data.name}` : d.data.name);

        totalWidth += textElement.node().getComputedTextLength() + 16;

        if (d.data.manager) {
          // Create a placeholder for the manager avatar
          const managerAvatarGroup = node.append("g")
            .attr("class", "manager-avatar-placeholder")
            .attr("transform", `translate(${totalWidth + 8}, ${barHeight / 2})`);
            
            // Add a default circle as placeholder
            managerAvatarGroup.append("circle")
              .attr("r", 8)
              .attr("fill", "#E2E8F0")
              .attr("stroke", "#94A3B8")
              .attr("stroke-width", 1);

            managerAvatarGroup.append("text")
              .attr("dy", "0.31em")
              .attr("text-anchor", "middle")
              .attr("fill", "#475569")
              .attr("font-size", "10px")
              .text(getInitials(d.data.manager.fullName));
            
            // Asynchronously load the manager photo without blocking
            if (d.data.manager.azureId) {
              employeesApi.getEmployeePhoto(d.data.manager.azureId)
                .then(photoUrl => {
                  if (photoUrl) {
                    // Remove the placeholder
                    managerAvatarGroup.selectAll("*").remove();
                    
                    const clipId = `clip-${Math.random().toString(36).substr(2, 9)}`;
                    
                    // Add clip path
                    managerAvatarGroup.append("defs")
                      .append("clipPath")
                      .attr("id", clipId)
                      .append("circle")
                      .attr("r", 8);
                      
                    // Add the image
                    managerAvatarGroup.append("image")
                      .attr("x", -8)
                      .attr("y", -8)
                      .attr("width", 16)
                      .attr("height", 16)
                      .attr("clip-path", `url(#${clipId})`)
                      .attr("preserveAspectRatio", "xMidYMid slice")
                      .attr("xlink:href", photoUrl);
                  }
                })
                .catch(error => {
                  console.error('Error loading manager photo:', error);
                  // Placeholder already rendered, no need to do anything
                });
            }

            totalWidth += 32;

            const managerText = node.append("text")
              .attr("x", totalWidth)
              .attr("y", barHeight / 2)
              .attr("dy", "0.31em")
              .attr("text-anchor", "start")
              .text(`(${d.data.manager.fullName})`);

            totalWidth += managerText.node().getComputedTextLength() + 24;
        }

        // Add action buttons
        const addButton = node.append("g")
          .attr("class", "add-subteam-button")
          .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
          .style("cursor", readOnly ? "default" : "pointer")
          .style("opacity", 0)
          .style("display", readOnly ? "none" : null)
          .on("click", (event) => {
            event.stopPropagation();
            if (readOnly) return;
            setSelectedTeam({
              name: '',
              projectId: projectId,
              parentTeamId: d.data.id
            });
            setSelectedManager(null);
            setShowTeamModal(true);
          });

        addButton.append("circle")
          .attr("r", 7)
          .attr("fill", "#2563EB")
          .attr("stroke", "none");

        addButton.append("text")
          .attr("dy", "0.31em")
          .attr("text-anchor", "middle")
          .attr("fill", "white")
          .attr("font-size", "12px")
          .text("+");

        totalWidth += 25;

        const editButton = node.append("g")
          .attr("class", "edit-button")
          .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
          .style("cursor", readOnly ? "default" : "pointer")
          .style("opacity", 0)
          .style("display", readOnly || d.depth === 0 ? "none" : null)
          .on("click", (event) => {
            event.stopPropagation();
            if (readOnly) return;
            setSelectedTeam({
              id: d.data.id,
              name: d.data.name,
              projectId: projectId,
              parentTeamId: d.parent?.data?.id,
              isRoot: d.depth === 0
            });
            setSelectedManager(d.data.manager || null);
            setShowTeamModal(true);
          });

        editButton.append("circle")
          .attr("r", 7)
          .attr("fill", "#F59E0B")
          .attr("stroke", "none");

        editButton.append("text")
          .attr("dy", "0.31em")
          .attr("text-anchor", "middle")
          .attr("fill", "white")
          .attr("font-size", "11px")
          .attr("font-weight", "bold")
          .text("e");

        totalWidth += 25;

        // History button for root node (project manager history)
        if (d.depth === 0) {
          const historyButton = node.append("g")
            .attr("class", "history-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", readOnly ? "default" : "pointer")
            .style("opacity", 0)
            .style("display", readOnly ? "none" : null)
            .on("click", (event) => {
              event.stopPropagation();
              if (readOnly) return;
              setHistoryModal({ open: true, entityId: projectId, entityName: d.data.name, entityType: MANAGER_HISTORY_ENTITY_TYPES.PROJECT });
            });

          historyButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#8B5CF6")
            .attr("stroke", "none");

          historyButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .attr("font-weight", "bold")
            .text("h");

          totalWidth += 25;
        }

        if (d.depth !== 0) {
          // Add move button for teams
          const moveTeamButton = node.append("g")
            .attr("class", "move-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", readOnly ? "default" : "pointer")
            .style("opacity", 0)
            .style("display", readOnly ? "none" : null)
            .on("click", (event) => {
              event.stopPropagation();
              if (readOnly) return;
              setTeamToMove({
                id: d.data.id,
                name: d.data.name
              });
              setShowMoveTeamModal(true);
            });

          moveTeamButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#0EA5E9")
            .attr("stroke", "none");

          moveTeamButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .text("m");

          totalWidth += 25;

          const deleteButton = node.append("g")
            .attr("class", "delete-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", readOnly ? "default" : "pointer")
            .style("opacity", 0)
            .style("display", readOnly ? "none" : null)
            .on("click", (event) => {
              event.stopPropagation();
              if (readOnly) return;
              setItemToDelete({ 
                id: d.data.id, 
                name: d.data.name,
                type: 'team'
              });
              setShowDeleteConfirmation(true);
            });

          deleteButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#DC2626")
            .attr("stroke", "none");

          deleteButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "12px")
            .text("×");

          totalWidth += 25;

          // History button for team (team manager history)
          const historyButton = node.append("g")
            .attr("class", "history-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", readOnly ? "default" : "pointer")
            .style("opacity", 0)
            .style("display", readOnly ? "none" : null)
            .on("click", (event) => {
              event.stopPropagation();
              if (readOnly) return;
              setHistoryModal({ open: true, entityId: d.data.id, entityName: d.data.name, entityType: MANAGER_HISTORY_ENTITY_TYPES.TEAM });
            });

          historyButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#8B5CF6")
            .attr("stroke", "none");

          historyButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .attr("font-weight", "bold")
            .text("h");

          totalWidth += 25;
        }
      });

      const nodeUpdate = node.merge(nodeEnter)
        .transition()
        .duration(300)
        .attr("transform", d => `translate(0,${d.y})`)
        .style("opacity", 1);

      node.select(".button-group text")
        .text(d => d.children ? "−" : "+");

      nodeUpdate.select(".add-subteam-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") ? 1 : 0;
        });
      
      nodeUpdate.select(".edit-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") ? 1 : 0;
        });

      nodeUpdate.select(".move-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") ? 1 : 0;
        });

      nodeUpdate.select(".delete-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") && d.depth !== 0 ? 1 : 0;
        });

      nodeUpdate.select(".history-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") ? 1 : 0;
        });

      const nodeExit = node.exit()
        .transition()
        .duration(300)
        .attr("transform", d => `translate(0,${source.y})`)
        .style("opacity", 0)
        .remove();

      nodes.forEach(d => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    };

    updateTree(root);

  }, [teamHierarchy, expandAll, readOnly]);

  if (isTeamHierarchyLoading) {
    return <LoadingSpinner />;
  }

  if (!teamHierarchy) {
    return (
      <FormContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <HeaderButton onClick={() => {
            setSelectedTeam({
              name: '',
              projectId: projectId,
              parentTeamId: null
            });
            setSelectedManager(null);
            setShowTeamModal(true);
          }}>
            <FaPlus /> Add Team
          </HeaderButton>
        </Box>
        <EmptyState
          title="No Teams Found"
          description="Click the 'Add Team' button to create your first team in this project."
        />
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Content>
        <Box 
          sx={{ 
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
            p: 2,
          }}
        >
          <svg 
            ref={svgRef} 
            style={{ 
              width: '100%', 
              minWidth: '800px', 
              minHeight: teamHierarchy && (!teamHierarchy.childTeams || teamHierarchy.childTeams.length === 0) && (teamHierarchy.employees.length === 0) ? '100px' : '600px'
            }}
          />
        </Box>
        {teamHierarchy && (!teamHierarchy.childTeams || teamHierarchy.childTeams.length === 0) && (teamHierarchy.employees.length === 0) && (
          <Grid item xs={12}>
            <EmptyState
              title="No Teams Found"
              description="Click on the project in the tree and use the '+' button to add teams or sub-teams to this project."
            />
          </Grid>
        )}
      </Content>

      <Modal
        isOpen={showTeamModal}
        onClose={handleTeamModalClose}
        title={selectedTeam?.id ? (selectedTeam?.isRoot ? 'Edit Root Team Representative' : 'Edit Team') : 'Create New Team'}
        onSubmit={handleTeamSubmit}
        submitText={selectedTeam?.id ? 'Update Team' : 'Create Team'}
        submitDisabled={!selectedTeam?.isRoot && !selectedTeam?.name}
        cancelText="Cancel"
        submitButtonProps={{
          sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
        }}
        cancelButtonProps={{
          sx: { backgroundColor: '#F1F5F9', color: '#475569', '&:hover': { backgroundColor: '#E2E8F0' } }
        }}
      >
        {(!selectedTeam?.isRoot || !selectedTeam?.id) && (
          <Field>
            <TextField
              fullWidth
              label="Team Name"
              placeholder="Enter team name"
              value={selectedTeam?.name || ''}
              onChange={(e) => handleTeamChange('name', e.target.value)}
              required
              disabled={selectedTeam?.isRoot}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  '& fieldset': { borderColor: '#E2E8F0' },
                  '&:hover fieldset': { borderColor: '#CBD5E1' },
                  '&.Mui-focused fieldset': { borderColor: '#2563EB' }
                }
              }}
            />
          </Field>
        )}

        <Field>
          <Box sx={{ width: '100%' }}>
            <EmployeeSearch
              value={selectedManager}
              onChange={setSelectedManager}
              label="Team Representative"
              placeholder="No representative assigned"
              width="100%"
              height="48px"
              showSelectedAvatar={true}
              disabled={true}
              sx={{ width: '100%' }}
            />
            <Typography variant="body2" sx={{ mt: 1, color: '#94A3B8', fontSize: '12px' }}>
              To assign or change the representative, use the history icon
              <Box component="span" sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                backgroundColor: '#8B5CF6',
                color: 'white',
                fontSize: '9px',
                fontWeight: 'bold',
                lineHeight: 1,
                verticalAlign: 'middle',
                mx: '4px'
              }}>h</Box>
              on the team node in the tree.
            </Typography>
          </Box>
        </Field>
      </Modal>

      <Modal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setItemToDelete(null);
        }}
        onSubmit={handleDeleteConfirm}
        title="Delete Team"
        submitText="Delete"
        cancelText="Cancel"
        submitButtonProps={{
          sx: { backgroundColor: '#DC2626', color: '#FFFFFF', '&:hover': { backgroundColor: '#B91C1C' } }
        }}
      >
        <Typography sx={{ mb: 2 }}>
          Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
        </Typography>
      </Modal>

      {/* Add Move Employee Modal */}
      <Modal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setEmployeeToMove(null);
          setSelectedDestinationTeam(null);
        }}
        title={`Move ${employeeToMove?.name}`}
        onSubmit={handleMoveEmployee}
        submitText="Move Individual"
        submitDisabled={!selectedDestinationTeam}
        cancelText="Cancel"
        submitButtonProps={{
          sx: { backgroundColor: '#0EA5E9', color: '#FFFFFF', '&:hover': { backgroundColor: '#0284C7' } }
        }}
      >
        <Field>
          <Label>Select Destination Team</Label>
          <FormControl fullWidth>
            <Select
              value={selectedDestinationTeam || ''}
              onChange={(e) => setSelectedDestinationTeam(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select a team</em>
              </MenuItem>
              {teamHierarchy && getAllTeams(teamHierarchy)
                .map(team => (
                  <MenuItem 
                    key={team.id} 
                    value={team.id}
                    disabled={team.id === employeeToMove?.currentTeamId}
                    sx={{ 
                      pl: 2 + team.level * 3, // Base padding 16px (pl: 2) + 24px (3) per level
                      display: 'flex',
                      alignItems: 'center',
                      color: team.id === employeeToMove?.currentTeamId ? '#94A3B8' : 'inherit',
                      '&.Mui-disabled': {
                        opacity: 0.7,
                        backgroundColor: '#F1F5F9',
                        '&:hover': {
                          backgroundColor: '#F1F5F9'
                        }
                      }
                    }}
                  >
                    {team.level > 0 && (
                      <Box
                        component="span"
                        sx={{
                          width: '16px',
                          height: '1px',
                          backgroundColor: team.id === employeeToMove?.currentTeamId ? '#CBD5E1' : '#94A3B8',
                          display: 'inline-block',
                          mr: 1
                        }}
                      />
                    )}
                    {team.name}
                    {team.id === employeeToMove?.currentTeamId && (
                      <Typography
                        component="span"
                        sx={{
                          ml: 1,
                          fontSize: '0.75rem',
                          color: '#94A3B8',
                          fontStyle: 'italic'
                        }}
                      >
                        (Current Team)
                      </Typography>
                    )}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Field>
      </Modal>

      {/* Add Move Team Modal */}
      <Modal
        isOpen={showMoveTeamModal}
        onClose={() => {
          setShowMoveTeamModal(false);
          setTeamToMove(null);
          setSelectedDestinationParentTeam(null);
        }}
        title={`Move ${teamToMove?.name}`}
        onSubmit={handleMoveTeam}
        submitText="Move Team"
        submitDisabled={!selectedDestinationParentTeam}
        cancelText="Cancel"
        submitButtonProps={{
          sx: { backgroundColor: '#0EA5E9', color: '#FFFFFF', '&:hover': { backgroundColor: '#0284C7' } }
        }}
      >
        <Field>
          <Label>Select Destination Parent Team</Label>
          <FormControl fullWidth>
            <Select
              value={selectedDestinationParentTeam || ''}
              onChange={(e) => setSelectedDestinationParentTeam(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select a parent team</em>
              </MenuItem>
              {teamHierarchy && (() => {
                const allTeams = getAllTeams(teamHierarchy);
                return allTeams.map(team => {
                  const isDisabled = shouldDisableTeamForMove(team, teamToMove, allTeams);
                  const disabledReason = team.id === teamToMove?.id ? 
                    'Current Team' : 
                    isDisabled ? 'Child Team' : null;

                  return (
                    <MenuItem 
                      key={team.id} 
                      value={team.id}
                      disabled={isDisabled}
                      sx={{ 
                        pl: 2 + team.level * 3, // Base padding 16px (pl: 2) + 24px (3) per level
                        display: 'flex',
                        alignItems: 'center',
                        color: isDisabled ? '#94A3B8' : 'inherit',
                        '&.Mui-disabled': {
                          opacity: 0.7,
                          backgroundColor: '#F1F5F9',
                          '&:hover': {
                            backgroundColor: '#F1F5F9'
                          }
                        }
                      }}
                    >
                      {team.level > 0 && (
                        <Box
                          component="span"
                          sx={{
                            width: '16px',
                            height: '1px',
                            backgroundColor: isDisabled ? '#CBD5E1' : '#94A3B8',
                            display: 'inline-block',
                            mr: 1
                          }}
                        />
                      )}
                      {team.name}
                      {disabledReason && (
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: '0.75rem',
                            color: '#94A3B8',
                            fontStyle: 'italic'
                          }}
                        >
                          ({disabledReason})
                        </Typography>
                      )}
                    </MenuItem>
                  );
                });
              })()}
            </Select>
          </FormControl>
        </Field>
      </Modal>

      <ManagerHistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, entityId: null, entityName: '', entityType: null })}
        entityId={historyModal.entityId}
        entityName={historyModal.entityName}
        entityType={historyModal.entityType}
        onUpdate={fetchTeamHierarchy}
        onCurrentManagerChange={historyModal.entityType === MANAGER_HISTORY_ENTITY_TYPES.PROJECT ? onProjectManagerChange : undefined}
      />
    </FormContainer>
  );
};

export default TeamStructure;
